import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  Users, 
  TrendingUp, 
  DollarSign,
  Crown,
  Star,
  Sparkles,
  ArrowUpCircle,
  ArrowDownCircle,
  Calendar,
  Clock,
  Eye,
  UserCheck,
  Percent
} from 'lucide-react';
import { useAdminPlansData, PlanStatsItem } from '@/hooks/useAdminPlansData';

const PlansManagement = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const { planStats, recentActivity, loading } = useAdminPlansData();

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-8 max-w-7xl">
        <div className="space-y-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[...Array(4)].map((_, i) => (
              <Card key={i} className="bg-white/5 backdrop-blur-sm border-white/10">
                <CardContent className="p-4">
                  <Skeleton className="h-16 w-full bg-white/10" />
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </div>
    );
  }

  const { totals, sponsors, sponsored } = planStats;

  const StatCard = ({ 
    title, 
    value, 
    icon: Icon, 
    color = 'text-white',
    bgColor = 'bg-white/10'
  }: { 
    title: string; 
    value: string | number; 
    icon: any;
    color?: string;
    bgColor?: string;
  }) => (
    <Card className="bg-white/5 backdrop-blur-sm border-white/10">
      <CardContent className="p-4">
        <div className="flex items-center gap-3">
          <div className={`p-2 ${bgColor} rounded-lg`}>
            <Icon className={`h-5 w-5 ${color}`} />
          </div>
          <div>
            <p className="text-white/60 text-sm">{title}</p>
            <p className="text-white text-xl font-bold">{value}</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );

  const PlanCard = ({
    name,
    icon: Icon,
    iconColor,
    borderColor,
    stats,
    badge
  }: {
    name: string;
    icon: any;
    iconColor: string;
    borderColor: string;
    stats: PlanStatsItem;
    badge?: string;
  }) => (
    <Card className={`bg-white/5 backdrop-blur-sm ${borderColor} hover:bg-white/10 transition-all`}>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-white flex items-center gap-2">
            <Icon className={`h-5 w-5 ${iconColor}`} />
            {name}
            {badge && <Badge className="bg-luxury-pinkBright/20 text-luxury-pinkBright text-xs">{badge}</Badge>}
          </CardTitle>
          <Badge variant="secondary" className="bg-white/10 text-white">
            {stats.users} usuários
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex justify-between text-sm">
          <span className="text-white/60">Receita (30d)</span>
          <span className="text-white font-medium">
            R$ {stats.revenue.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
          </span>
        </div>
        <div className="flex justify-between text-sm">
          <span className="text-white/60">Conversões (30d)</span>
          <span className="text-green-400 font-medium">+{stats.conversions30d}</span>
        </div>
      </CardContent>
    </Card>
  );

  const billingPeriodLabel = (period: string) => {
    switch (period) {
      case 'weekly': return 'Semanal';
      case 'monthly': return 'Mensal';
      case 'quarterly': return 'Trimestral';
      default: return period;
    }
  };

  return (
    <div className="container mx-auto px-4 py-8 max-w-7xl">
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold text-white mb-2">Gerenciamento de Planos</h1>
          <p className="text-white/60">Visão geral das assinaturas e receita</p>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard 
            title="Total Usuários" 
            value={totals.totalUsers.toLocaleString()} 
            icon={Users}
            color="text-blue-400"
            bgColor="bg-blue-500/20"
          />
          <StatCard 
            title="Assinantes Pagos" 
            value={totals.totalPaying.toLocaleString()} 
            icon={UserCheck}
            color="text-green-400"
            bgColor="bg-green-500/20"
          />
          <StatCard 
            title="Receita Mensal (30d)" 
            value={`R$ ${totals.monthlyRecurringRevenue.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`}
            icon={DollarSign}
            color="text-luxury-pinkBright"
            bgColor="bg-luxury-pinkBright/20"
          />
          <StatCard 
            title="Taxa de Conversão" 
            value={`${totals.conversionRate.toFixed(1)}%`}
            icon={Percent}
            color="text-purple-400"
            bgColor="bg-purple-500/20"
          />
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-white/5 border border-white/10">
            <TabsTrigger 
              value="overview"
              className="data-[state=active]:bg-luxury-pinkBright data-[state=active]:text-white text-white/60"
            >
              <Eye className="w-4 h-4 mr-2" />
              Visão Geral
            </TabsTrigger>
            <TabsTrigger 
              value="sponsors"
              className="data-[state=active]:bg-luxury-pinkBright data-[state=active]:text-white text-white/60"
            >
              <Crown className="w-4 h-4 mr-2" />
              Sponsors
            </TabsTrigger>
            <TabsTrigger 
              value="sponsored"
              className="data-[state=active]:bg-luxury-pinkBright data-[state=active]:text-white text-white/60"
            >
              <Star className="w-4 h-4 mr-2" />
              Sponsored
            </TabsTrigger>
            <TabsTrigger 
              value="activity"
              className="data-[state=active]:bg-luxury-pinkBright data-[state=active]:text-white text-white/60"
            >
              <TrendingUp className="w-4 h-4 mr-2" />
              Atividade
            </TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="mt-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Sponsors Summary */}
              <Card className="bg-white/5 backdrop-blur-sm border-white/10">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <Crown className="h-5 w-5 text-luxury-pinkBright" />
                    Sugar Daddies / Mommies
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-3 gap-4 text-center">
                    <div className="p-3 bg-white/5 rounded-lg">
                      <p className="text-white/60 text-xs">Intro (Free)</p>
                      <p className="text-white text-xl font-bold">{sponsors.intro.users}</p>
                    </div>
                    <div className="p-3 bg-white/5 rounded-lg border border-luxury-pinkBright/30">
                      <p className="text-white/60 text-xs">Inspire</p>
                      <p className="text-white text-xl font-bold">{sponsors.inspire.users}</p>
                    </div>
                    <div className="p-3 bg-white/5 rounded-lg border border-luxury-pinkBright/50">
                      <p className="text-white/60 text-xs">Infinity</p>
                      <p className="text-white text-xl font-bold">{sponsors.infinity.users}</p>
                    </div>
                  </div>
                  <div className="flex justify-between text-sm pt-2 border-t border-white/10">
                    <span className="text-white/60">Total Sponsors</span>
                    <span className="text-white font-medium">
                      {sponsors.intro.users + sponsors.inspire.users + sponsors.infinity.users}
                    </span>
                  </div>
                </CardContent>
              </Card>

              {/* Sponsored Summary */}
              <Card className="bg-white/5 backdrop-blur-sm border-white/10">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <Star className="h-5 w-5 text-luxury-pinkBright" />
                    Sugar Babies
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-4 text-center">
                    <div className="p-3 bg-white/5 rounded-lg">
                      <p className="text-white/60 text-xs">Intro (Free)</p>
                      <p className="text-white text-xl font-bold">{sponsored.intro.users}</p>
                    </div>
                    <div className="p-3 bg-white/5 rounded-lg border border-luxury-pinkBright/50">
                      <p className="text-white/60 text-xs">Infinity</p>
                      <p className="text-white text-xl font-bold">{sponsored.infinity.users}</p>
                    </div>
                  </div>
                  <div className="flex justify-between text-sm pt-2 border-t border-white/10">
                    <span className="text-white/60">Total Sponsored</span>
                    <span className="text-white font-medium">
                      {sponsored.intro.users + sponsored.infinity.users}
                    </span>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Sponsors Tab */}
          <TabsContent value="sponsors" className="mt-6">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <PlanCard
                name="Intro"
                icon={Users}
                iconColor="text-gray-400"
                borderColor="border-white/10"
                stats={sponsors.intro}
                badge="Free"
              />
              <PlanCard
                name="Inspire"
                icon={Sparkles}
                iconColor="text-luxury-pinkBright"
                borderColor="border-luxury-pinkBright/30"
                stats={sponsors.inspire}
              />
              <PlanCard
                name="Infinity"
                icon={Crown}
                iconColor="text-luxury-pinkBright"
                borderColor="border-luxury-pinkBright/50"
                stats={sponsors.infinity}
                badge="Premium"
              />
            </div>
          </TabsContent>

          {/* Sponsored Tab */}
          <TabsContent value="sponsored" className="mt-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <PlanCard
                name="Intro"
                icon={Users}
                iconColor="text-gray-400"
                borderColor="border-white/10"
                stats={sponsored.intro}
                badge="Free"
              />
              <PlanCard
                name="Infinity"
                icon={Star}
                iconColor="text-luxury-pinkBright"
                borderColor="border-luxury-pinkBright/50"
                stats={sponsored.infinity}
                badge="Premium"
              />
            </div>
          </TabsContent>

          {/* Activity Tab */}
          <TabsContent value="activity" className="mt-6">
            <Card className="bg-white/5 backdrop-blur-sm border-white/10">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Clock className="h-5 w-5" />
                  Atividade Recente de Planos
                </CardTitle>
              </CardHeader>
              <CardContent>
                {recentActivity.length === 0 ? (
                  <div className="text-center py-8">
                    <div className="w-16 h-16 bg-white/10 rounded-full flex items-center justify-center mx-auto mb-4">
                      <TrendingUp className="w-8 h-8 text-white/40" />
                    </div>
                    <p className="text-white/60 text-sm">Nenhuma atividade recente</p>
                    <p className="text-white/40 text-xs mt-1">As mudanças de plano aparecerão aqui</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {recentActivity.map((activity) => (
                      <div 
                        key={activity.id} 
                        className="flex items-center justify-between p-3 bg-white/5 rounded-lg border border-white/5"
                      >
                        <div className="flex items-center gap-3">
                          <div className={`p-2 rounded-lg ${
                            activity.action === 'upgrade' ? 'bg-green-500/20' : 'bg-orange-500/20'
                          }`}>
                            {activity.action === 'upgrade' ? 
                              <ArrowUpCircle className="h-4 w-4 text-green-400" /> : 
                              <ArrowDownCircle className="h-4 w-4 text-orange-400" />
                            }
                          </div>
                          <div>
                            <p className="text-white text-sm font-medium">{activity.user}</p>
                            <p className="text-white/60 text-xs">
                              {activity.fromCommercial} → {activity.toCommercial}
                            </p>
                            {activity.amount > 0 && (
                              <p className="text-white/40 text-xs">
                                R$ {activity.amount.toFixed(2)} • {billingPeriodLabel(activity.billingPeriod)}
                              </p>
                            )}
                          </div>
                        </div>
                        <div className="text-right">
                          <Badge 
                            className={activity.action === 'upgrade' 
                              ? 'bg-green-500/20 text-green-400' 
                              : 'bg-orange-500/20 text-orange-400'
                            }
                          >
                            {activity.action === 'upgrade' ? 'Upgrade' : 'Downgrade'}
                          </Badge>
                          <p className="text-white/40 text-xs flex items-center gap-1 mt-1 justify-end">
                            <Calendar className="h-3 w-3" />
                            {activity.date}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default PlansManagement;
